import React, { useEffect, useState } from 'react';
import { KeyRound, Lock, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, RefreshCw, Info } from 'lucide-react';
import { obtenerConfigCliente, ApisCredenciales, ApiDefinition } from '../../services/clienteConexionService.ts';
import { ApiConnectionCredentials } from './apiCredentials.types.ts';

interface CredencialesApiPanelProps {
  clienteId?: string;
  clienteNombre?: string;
  credentials: ApiConnectionCredentials;
  onChange: (credentials: ApiConnectionCredentials) => void;
}

const aCredentials = (api: ApiDefinition): ApiConnectionCredentials => ({
  clientId: api.clientId || '',
  dataCenter: api.dataCenter || '',
  realm: api.realm || '',
  anid: api.anid || '',
  apiKey: '',
  oauthSecret: ''
});

export default function CredencialesApiPanel({ clienteId, clienteNombre, credentials, onChange }: CredencialesApiPanelProps) {
  const [expanded, setExpanded] = useState(!credentials.clientId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apis, setApis] = useState<ApiDefinition[]>([]);
  const [apiSeleccionadaId, setApiSeleccionadaId] = useState<string>('');

  useEffect(() => {
    if (!clienteId) return;
    setLoading(true);
    setError(null);
    obtenerConfigCliente<ApisCredenciales>(clienteId, 'apis')
      .then((config) => {
        // Este panel es específicamente para APIs de Ariba (OAuth2) — las
        // de S/4HANA (usuario/contraseña) se prueban desde su propio
        // explorador. Entradas guardadas antes de que existiera "tipo" se
        // asumen Ariba, igual que en el backend (ver configService.js).
        const lista = (config.apis || []).filter((api) => (api.tipo || 'ariba') === 'ariba');
        setApis(lista);
        const primera = lista[0];
        setApiSeleccionadaId(primera?.id || '');
        onChange(primera ? aCredentials(primera) : { clientId: '', dataCenter: '', realm: '', anid: '', apiKey: '', oauthSecret: '' });
      })
      .catch((err) => {
        console.error('Error cargando credenciales guardadas:', err);
        setError('No se pudo cargar la configuración guardada de este cliente.');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const apiSeleccionada = apis.find((api) => api.id === apiSeleccionadaId);

  const handleSeleccionarApi = (id: string) => {
    setApiSeleccionadaId(id);
    const api = apis.find((a) => a.id === id);
    if (api) onChange(aCredentials(api));
  };

  const update = (field: keyof ApiConnectionCredentials) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...credentials, [field]: e.target.value });
  };

  const isConfigured = Boolean(credentials.clientId && credentials.dataCenter);
  const inputClass = "w-full bg-black/30 border border-white/10 rounded-lg p-2.5 text-sm text-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <section className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 mb-10">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-4 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="p-2 bg-blue-500/20 rounded-xl text-blue-400 flex-shrink-0">
            <KeyRound className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-white">Credenciales de conexión (Ariba)</h3>
            <p className="text-xs text-gray-400 truncate">
              {clienteId
                ? `Cargadas desde la Configuración de ${clienteNombre || 'este cliente'}`
                : 'Ingresa los datos manualmente para esta sesión'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {isConfigured ? (
            <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-2.5 py-1 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5" /> Configurado
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded-full">
              <AlertTriangle className="w-3.5 h-3.5" /> Sin configurar
            </span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="mt-6">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Cargando configuración guardada...
            </div>
          )}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs mb-4">{error}</div>
          )}

          {clienteId ? (
            apis.length === 0 ? (
              <div className="bg-amber-500/10 border border-amber-500/20 text-amber-300 p-3 rounded-lg text-xs flex items-start gap-2">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Este cliente todavía no tiene ninguna API de Ariba configurada. Ve a Configuración del proyecto para agregar una.</span>
              </div>
            ) : (
              <>
                {apis.length > 1 && (
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-400 mb-1">API</label>
                    <select
                      className="w-full bg-black/30 border border-white/10 rounded-lg p-2.5 text-sm text-white outline-none focus:border-blue-500"
                      value={apiSeleccionadaId}
                      onChange={(e) => handleSeleccionarApi(e.target.value)}
                    >
                      {apis.map((api) => (
                        <option key={api.id} value={api.id}>{api.nombre || 'Sin nombre'}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2 mb-4">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    {apiSeleccionada?.tieneCredencialesGuardadas
                      ? 'El API Key y OAuth Secret de esta API ya están guardados de forma segura y no se muestran aquí. '
                      : 'Aún no hay API Key ni OAuth Secret guardados para esta API. '}
                    Para editar estos datos ve a Configuración del proyecto.
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ReadOnlyField label="Client ID" value={credentials.clientId} />
                  <ReadOnlyField label="Data Center" value={credentials.dataCenter} />
                  <ReadOnlyField label="Realm" value={credentials.realm} />
                  <ReadOnlyField label="ANID" value={credentials.anid} />
                </div>
              </>
            )
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelClass}>Client ID</label>
                  <input className={inputClass} value={credentials.clientId} onChange={update('clientId')} placeholder="Client ID" />
                </div>
                <div>
                  <label className={labelClass}>Data Center</label>
                  <input className={inputClass} value={credentials.dataCenter} onChange={update('dataCenter')} placeholder="Ej: eu, us" />
                </div>
                <div>
                  <label className={labelClass}>Realm</label>
                  <input className={inputClass} value={credentials.realm} onChange={update('realm')} placeholder="Realm" />
                </div>
                <div>
                  <label className={labelClass}>ANID</label>
                  <input className={inputClass} value={credentials.anid} onChange={update('anid')} placeholder="ANID" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}><Lock className="w-3 h-3 text-amber-400 inline mr-1" />API Key</label>
                  <input type="password" className={inputClass} value={credentials.apiKey} onChange={update('apiKey')} placeholder="••••••••" />
                </div>
                <div>
                  <label className={labelClass}><Lock className="w-3 h-3 text-amber-400 inline mr-1" />OAuth Secret</label>
                  <input type="password" className={inputClass} value={credentials.oauthSecret} onChange={update('oauthSecret')} placeholder="••••••••" />
                </div>
              </div>
              <p className="text-[11px] text-gray-500 mt-3">
                Estos datos se usan solo durante esta sesión de pruebas y no se guardan en el sistema.
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1">{label}</label>
      <div className="w-full bg-black/20 border border-white/5 rounded-lg p-2.5 text-sm text-gray-300">
        {value || <span className="text-gray-600 italic">No configurado</span>}
      </div>
    </div>
  );
}
